/**
 * keyboard-shortcuts.tsx — Global keyboard shortcut listener + help overlay.
 *
 * Registers window-level keydown events for:
 *   /        → onSearch
 *   n        → onNewTask
 *   g then d/t/p/o/h/n/f/i/c/j (1.5s chord) → onGoto view
 *   ?        → toggle help overlay
 *   Escape   → onCloseAll
 *
 * Ignores shortcuts while focus is inside input/textarea/select/contenteditable.
 * Renders a Vietnamese help overlay toggled by `?`, closable by Esc or backdrop click.
 * Accepts optional `open` + `onOpenChange` props for external control (e.g. shell header button).
 */

/* global EventTarget, HTMLElement, KeyboardEvent */

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

export interface KeyboardShortcutHandlers {
  onSearch: () => void;
  onNewTask: () => void;
  onGoto: (view: string) => void;
  onCloseAll: () => void;
}

export interface KeyboardShortcutsProps {
  handlers: KeyboardShortcutHandlers;
  /** External open state — when provided, syncs with internal toggle. */
  open?: boolean;
  /** Called when internal state wants to change open (e.g. ? key, Esc). */
  onOpenChange?: (open: boolean) => void;
}

// Chord: g → then one of these keys to navigate (all 10 views, non-conflicting)
// Mnemonics: d=dashboard/today, t=tasks, p=projects, o=objectives/goals,
//            h=habits, n=notes, f=finance, i=inbox, c=calendar, j=journal
const GOTO_KEYS: Record<string, string> = {
  d: "today",
  t: "tasks",
  p: "projects",
  o: "goals",
  h: "habits",
  n: "notes",
  f: "finance",
  i: "inbox",
  c: "calendar",
  j: "journal",
};

const CHORD_TIMEOUT_MS = 1500;

// ── Shortcut definitions (for help overlay) ──────────────────────────────────

const SHORTCUTS = [
  { keys: ["/"], label: "Mở tìm kiếm" },
  { keys: ["n"], label: "Thêm việc mới" },
  { keys: ["g", "d"], label: "Đến Hôm nay" },
  { keys: ["g", "t"], label: "Đến Công việc" },
  { keys: ["g", "p"], label: "Đến Dự án" },
  { keys: ["g", "o"], label: "Đến Mục tiêu" },
  { keys: ["g", "h"], label: "Đến Thói quen" },
  { keys: ["g", "n"], label: "Đến Ghi chú" },
  { keys: ["g", "f"], label: "Đến Tài chính" },
  { keys: ["g", "i"], label: "Đến Hộp thư" },
  { keys: ["g", "c"], label: "Đến Lịch" },
  { keys: ["g", "j"], label: "Đến Nhật ký" },
  { keys: ["?"], label: "Hiện phím tắt này" },
  { keys: ["Esc"], label: "Đóng / Thoát" },
];

// ── Helper ───────────────────────────────────────────────────────────────────

/** Returns true when the event target is a text-entry element. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

// ── Component ────────────────────────────────────────────────────────────────

export function KeyboardShortcuts({ handlers, open: externalOpen, onOpenChange }: KeyboardShortcutsProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const chordPending = useRef(false);
  const chordTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When external open prop changes, sync internal state
  useEffect(() => {
    if (externalOpen !== undefined) {
      setInternalOpen(externalOpen);
    }
  }, [externalOpen]);

  // Derive effective visibility: external prop takes precedence when provided
  const showHelp = externalOpen !== undefined ? externalOpen : internalOpen;

  const setShowHelp = (next: boolean) => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    const clearChord = () => {
      chordPending.current = false;
      if (chordTimer.current !== null) {
        clearTimeout(chordTimer.current);
        chordTimer.current = null;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Never intercept when a modifier key (except Shift for ?) is held
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Ignore while typing
      if (isTypingTarget(e.target)) return;

      const key = e.key;

      // ── Chord second key ────────────────────────────────────────
      if (chordPending.current) {
        clearChord();
        const view = GOTO_KEYS[key];
        if (view) {
          e.preventDefault();
          handlers.onGoto(view);
        }
        return;
      }

      // ── Single-key shortcuts ─────────────────────────────────────
      switch (key) {
        case "/":
          e.preventDefault();
          handlers.onSearch();
          break;

        case "n":
          e.preventDefault();
          handlers.onNewTask();
          break;

        case "g":
          e.preventDefault();
          chordPending.current = true;
          chordTimer.current = setTimeout(clearChord, CHORD_TIMEOUT_MS);
          break;

        case "?":
          e.preventDefault();
          setShowHelp(!showHelp);
          break;

        case "Escape":
          if (showHelp) {
            setShowHelp(false);
          } else {
            handlers.onCloseAll();
          }
          break;

        default:
          break;
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", handleKeyDown);
      }
      clearChord();
    };
  // handlers identity is stable when caller memoises; listing showHelp to
  // keep the Escape/help-toggle logic correct without stale closure.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHelp, handlers]);

  if (!showHelp) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/40 backdrop-blur-sm"
      onClick={() => setShowHelp(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Danh sách phím tắt"
    >
      <div
        className="fade-in w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-4">
          <h2 className="font-display text-[15px] font-bold text-ink-900">Phím tắt bàn phím</h2>
          <button
            aria-label="Đóng"
            onClick={() => setShowHelp(false)}
            className="grid h-7 w-7 place-items-center rounded-lg text-ink-400 hover:bg-ink-200/60 hover:text-ink-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Shortcut list */}
        <ul className="divide-y divide-ink-100 px-2 py-2">
          {SHORTCUTS.map((s) => (
            <li key={s.label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-[13px] text-ink-700">{s.label}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 && <span className="text-[11px] text-ink-400">then</span>}
                    <kbd className="rounded-md border border-ink-200 bg-ink-100 px-2 py-0.5 font-mono text-[12px] text-ink-700">
                      {k}
                    </kbd>
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>

        <p className="px-5 pb-4 pt-1 text-center text-[12px] text-ink-400">
          Phím tắt không hoạt động khi đang nhập liệu
        </p>
      </div>
    </div>
  );
}
